import React from 'react';
import styled from 'styled-components';

interface Tab {
  id: string;
  label: string;
  hasNewContent?: boolean;
}

interface TabNavigationProps {
  tabs: Tab[];
  activeTab: string;
  onTabChange: (tabId: string) => void;
}

const TabsContainer = styled.div`
  border-bottom: 2px solid ${({ theme }) => theme.colors.borderLight};
  margin-bottom: 1.5rem;
`;

const TabsList = styled.div`
  display: flex;
  background: ${({ theme }) => theme.colors.surfaceSecondary};
  border-radius: ${({ theme }) => theme.borderRadius.lg} ${({ theme }) => theme.borderRadius.lg} 0 0;
  overflow: hidden;
`;

const TabButton = styled.button<{ isActive: boolean }>`
  flex: 1;
  padding: 1rem 1.5rem;
  border: none;
  background: ${({ isActive, theme }) => isActive ? theme.colors.surface : 'transparent'};
  color: ${({ isActive, theme }) => isActive ? theme.colors.primary : theme.colors.textSecondary};
  font-weight: ${({ isActive }) => isActive ? 600 : 500};
  font-size: 0.95rem;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.base};
  position: relative;
  border-bottom: ${({ isActive, theme }) => isActive ? `3px solid ${theme.colors.primary}` : '3px solid transparent'};

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
    background: ${({ isActive, theme }) => isActive ? theme.colors.surface : 'rgba(37, 99, 235, 0.05)'};
  }

  &:focus {
    outline: none;
    color: ${({ theme }) => theme.colors.primary};
    background: ${({ isActive, theme }) => isActive ? theme.colors.surface : 'rgba(37, 99, 235, 0.05)'};
  }
`;

const NewContentIndicator = styled.div`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  width: 8px;
  height: 8px;
  background: ${({ theme }) => theme.colors.error};
  border-radius: 50%;
  animation: pulse 2s infinite;

  @keyframes pulse {
    0% {
      transform: scale(1);
      opacity: 1;
    }
    50% {
      transform: scale(1.2);
      opacity: 0.7;
    }
    100% {
      transform: scale(1);
      opacity: 1;
    }
  }
`;

const TabNavigation: React.FC<TabNavigationProps> = ({ tabs, activeTab, onTabChange }) => {
  return (
    <TabsContainer>
      <TabsList>
        {tabs.map((tab) => (
          <TabButton
            key={tab.id}
            isActive={activeTab === tab.id}
            onClick={() => onTabChange(tab.id)}
            type="button"
          >
            {tab.label}
            {tab.hasNewContent && <NewContentIndicator />}
          </TabButton>
        ))}
      </TabsList>
    </TabsContainer>
  );
};

export default TabNavigation;